import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Hexagon, MessageSquareCode, KeyRound, CreditCard, LogOut, Home } from "lucide-react";
import { AgentChat } from "@/components/workspace/AgentChat";
import { ApiKeysPanel } from "@/components/workspace/ApiKeysPanel";
import { BillingPanel } from "@/components/workspace/BillingPanel";

const TABS = [
  { id: "agent", label: "Digital Twin Agent", icon: MessageSquareCode },
  { id: "keys", label: "API Keys", icon: KeyRound },
  { id: "billing", label: "Billing & Plan", icon: CreditCard },
];

export default function Workspace() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("agent");

  useEffect(() => {
    if (!loading && !user && !window.location.hash?.includes("session_id=")) {
      login();
    }
  }, [loading, user, login]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background grid-pattern flex items-center justify-center" data-testid="workspace-loading">
        <Hexagon className="w-10 h-10 text-emerald-400 animate-pulse-dot" />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-background overflow-hidden" data-testid="workspace-page">
      <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-950/60 flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2.5 px-5 py-4 border-b border-slate-800">
          <Hexagon className="w-6 h-6 text-emerald-400" strokeWidth={2.2} />
          <span className="font-display text-lg font-bold text-white">Wiwi</span>
        </div>
        <nav className="flex lg:flex-col flex-1 p-3 gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ws-tab-${t.id}`}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                             : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent"}`}>
              <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 hidden lg:block">
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-slate-700" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-semibold">
                {user.name?.[0]}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm text-slate-200 truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{user.plan} plan</div>
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => navigate("/")} data-testid="ws-home-btn"
              className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 border border-slate-800 rounded-lg py-2 transition-colors">
              <Home className="w-3.5 h-3.5" /> Home
            </button>
            <button onClick={logout} data-testid="ws-logout-btn"
              className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-red-400 border border-slate-800 rounded-lg py-2 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-hidden">
        {tab === "agent" && <AgentChat />}
        {tab === "keys" && <ApiKeysPanel />}
        {tab === "billing" && <BillingPanel />}
      </main>
    </div>
  );
}
